import { BidType, PromotionTypeEnum } from "@krea/shared/types/common";

import { IFieldRequest } from "./fieldRequest";
import { ILenderApplication } from "./lenderApplication";

export enum LenderResponseTypeEnum {
  UNCONDITIONAL_BID = "UNCONDITIONAL_BID",
  CONDITIONAL_BID = "CONDITIONAL_BID",
}

export enum LenderResponseBenefitTypeEnum {
  NO_ADDITIONS_REQUIRED = "NO_ADDITIONS_REQUIRED",
  REDUCED_GUARANTOR_COMMITMENT = "REDUCED_GUARANTOR_COMMITMENT",
  NO_GUARANTOR_REQUIRED = "NO_GUARANTOR_REQUIRED",
  FREE_EARLY_PAYMENT = "FREE_EARLY_PAYMENT",
  NO_LEASING_DOWN_PAYMENT = "NO_LEASING_DOWN_PAYMENT",
  NO_LIMIT_FEE = "NO_LIMIT_FEE",
  NO_SETUP_FEE = "NO_SETUP_FEE",
  EU_LOAN = "EU_LOAN",
  NO_WITHDRAWAL_FEE = "NO_WITHDRAWAL_FEE",
  NO_RECOURSE = "NO_RECOURSE",
  NO_AMORTIZATION = "NO_AMORTIZATION",
}

export interface ILenderResponseBenefit {
  icon?: string;
  displayName: string;
  explanation: string;
  type: LenderResponseBenefitTypeEnum;
}

export interface IPromotion {
  type: PromotionTypeEnum;
  value: number;
}

export interface ICompanyLoanBid {
  type: BidType.COMPANY_LOAN;
  createdAt: string;
  id: string;
  monthlyCostWithoutSetupFee: number;
  amortizationMethodDisplayName: string;
  amortizationMethodDescription: string;
  totalLoanCost: number;
  monthlyCost: number;
  setupFee: number;
  postalFee: number;
  directDebitFee: number;
  paybackTimeMonths: number;
  amortizationMethod: string;
  lenderMonthlyInterest: number;
  earlyRepaymentFee: number;
  amount: number;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

export interface IFactoringBid {
  type: BidType.FACTORING;
  createdAt: string;
  id: string;
  purchaseLimit: number;
  purchasedAmountPercent: number;
  amount: number; // purchaseLimit * (purchasedAmountPercent / 100)
  commissionPercent: number;
  paymentTermsDays: number;
  handlingFeePerInvoice: number;
  monthlyFee: number;
  totalMonthlyCost: number;
  limitFeePerYearPercent: number;
  setupFee: number;
  withRegress: boolean;
  regressNumDaysAfterDueDate: number;
  paidOutPerInvoice: number;
  paidOutPerInvoicePercent: number;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

export interface ILeasingBid {
  type: BidType.LEASING;
  createdAt: string;
  id: string;
  amount: number;
  paybackTimeMonths: number;
  monthlyPayment: number;
  totalCost: number;
  downPayment: number;
  terminalValue: number;
  setupFee: number;
  contractType: string;
  postalFee: number;
  insuranceMonthlyCost: number;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
  averageMonthlyCost: number;
}

export interface IRealEstateBid {
  type: BidType.REAL_ESTATE;
  createdAt: string;
  id: string;
  monthlyCostWithoutSetupFee: number;
  amortizationMethodDisplayName: string;
  amortizationMethodDescription: string;
  totalLoanCost: number;
  monthlyCost: number;
  setupFee: number;
  postalFee: number;
  directDebitFee: number;
  paybackTimeMonths: number;
  amortizationTimeMonths: number | null;
  amortizationMethod: string;
  lenderMonthlyInterest: number;
  earlyRepaymentFee: number;
  amount: number;
  isRepayableWithoutFee: boolean;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

export interface IRevolvingCreditBid {
  type: BidType.REVOLVING_CREDIT;
  createdAt: string;
  id: string;
  earlyRepaymentFee: number;
  directDebitFee: number;
  amount: number;
  monthlyAmortisation: number;
  // using lenderMonthlyInterestRevolvingCredit via GraphQL alias instead of lenderMonthlyInterest, because of name/type conflicts in graphql
  lenderMonthlyInterestRevolvingCredit: number;
  withdrawalFee: number;
  setupFee: number;
  limitFeePerYear: number;
  postalFee: number;
  monthlyPayment: number;
  monthlyCost: number;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

// Used when bids data (for dashboard) have been fetched (deeper nested data included, such as .lenderResponse.lenderApplication.lenderCompany)
export type IResolvedBid =
  | ICompanyLoanBid
  | IFactoringBid
  | ILeasingBid
  | IRealEstateBid
  | IRevolvingCreditBid;

export interface ILenderResponse {
  id: string;
  updatedAt: string;
  createdAt: string;
  bids: IResolvedBid[];
  fieldRequests: IFieldRequest[];
  bid?: ICompanyLoanBid;
  invoicePurchaseBid?: IFactoringBid;
  leasingBid?: ILeasingBid;
  realEstateBid?: IRealEstateBid;
  revolvingCreditBid?: IRevolvingCreditBid;
  type: LenderResponseTypeEnum;
  benefits: ILenderResponseBenefit[];
  promotions?: IPromotion[];
  creditTermsFile?: {
    id: string;
    url: string;
    originalFileName: string;
    createdAt: DateString;
  };
  attractivenessScore?: number;
  lenderApplication: Omit<ILenderApplication, "loanApplication">;
  isRecommendedSelect: boolean;
  paidOutAt: string;
  eligibleForPaymentAt: DateString;
  signingUrl?: string;
  totalLoanAmount: number;
  numberOfRequiredGuarantors: number;
}

export interface IBids {
  id: string;
  recommendedLenderResponse?: { id: string };
  lenderResponses: ILenderResponse[];
}
